import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_BoxModel = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📦 CSS Box Model</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <strong>CSS Box Model</strong> describes how every element on a
        webpage is structured and how space is calculated. Each element is
        treated as a box, made up of:
        <br />
        <strong>Content → Padding → Border → Margin</strong>.
      </p>

      {/* Diagram */}
      <div className="tutorial-section">
        <h2 className="section-title">Box Model Structure</h2>
        <p>
          Every element’s total size is calculated as:
          <br />
          <code>total width = content + padding + border + margin</code>
        </p>
        <div
          style={{
            display: "inline-block",
            padding: "20px",
            border: "10px solid #0ea5e9",
            margin: "20px",
            background: "#e0f2fe",
          }}
        >
          <div
            style={{
              padding: "15px",
              border: "5px solid #15803d",
              background: "#dcfce7",
            }}
          >
            <div style={{ padding: "10px", background: "#fef3c7" }}>
              Content Area
            </div>
          </div>
        </div>
        <p style={{ fontSize: "0.95rem", fontStyle: "italic" }}>
          In this demo: Yellow = Content, Green = Padding, Blue = Border, White
          space = Margin
        </p>
      </div>

      {/* Content */}
      <div className="tutorial-section">
        <h2 className="section-title">1. Content</h2>
        <p>
          The <strong>content area</strong> is where text and images appear. Its
          size is controlled by <code>width</code> and <code>height</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  width: 200px;
  height: 100px;
}`}
        />
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#fde68a",
            border: "1px solid #333",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Content Box
        </div>
      </div>

      {/* Padding */}
      <div className="tutorial-section">
        <h2 className="section-title">2. Padding</h2>
        <p>
          <strong>Padding</strong> is the space between the content and the
          border. It inherits the background color of the element.
        </p>
        <CodeCopyBlock
          code={`div {
  padding: 20px;
}`}
        />
        <div
          style={{
            padding: "20px",
            background: "#dcfce7",
            border: "2px solid #15803d",
          }}
        >
          Padded Box (20px)
        </div>
      </div>

      {/* Border */}
      <div className="tutorial-section">
        <h2 className="section-title">3. Border</h2>
        <p>
          <strong>Border</strong> wraps around the padding and content. It can
          have width, style, and color.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 5px solid blue;
}`}
        />
        <div
          style={{
            border: "5px solid blue",
            padding: "10px",
            background: "#f3f4f6",
          }}
        >
          Box with 5px border
        </div>
      </div>

      {/* Margin */}
      <div className="tutorial-section">
        <h2 className="section-title">4. Margin</h2>
        <p>
          <strong>Margin</strong> is the space <em>outside</em> the border. It
          pushes the element away from others.
        </p>
        <CodeCopyBlock
          code={`div {
  margin: 30px;
}`}
        />
        <div
          style={{
            margin: "30px",
            border: "2px dashed #f59e0b",
            padding: "10px",
            background: "#fef3c7",
          }}
        >
          Box with 30px margin
        </div>
      </div>

      {/* Box-sizing */}
      <div className="tutorial-section">
        <h2 className="section-title">Box-Sizing Property</h2>
        <p>
          By default, width/height apply only to the content box. To include{" "}
          <strong>padding and border</strong> inside the set width/height, use{" "}
          <code>box-sizing: border-box;</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  width: 200px;
  padding: 20px;
  border: 10px solid green;
  box-sizing: border-box;
}`}
        />
        <div
          style={{
            width: "200px",
            padding: "20px",
            border: "10px solid green",
            background: "#bbf7d0",
            boxSizing: "border-box",
          }}
        >
          This box is exactly 200px wide (padding & border included).
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Understanding the box model is essential for layout. Next, you should
          explore{" "}
          <a href="/css/display" className="learn-more">
            CSS Display
          </a>{" "}
          and{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          to learn how boxes interact in layouts.
        </p>
      </div>
    </div>
  );
};

export default CSS_BoxModel;
